import { ReactNode, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useRequestCountryCode from '@alltrails/context/hooks/useRequestCountryCode';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { useExperiments } from '@alltrails/experiments';
import { useModalAriaLabelledBy } from '@alltrails/modal';
import CheckoutBase from '@alltrails/payment/components/CheckoutBase';
import PaymentFinePrint from '@alltrails/payment/components/PaymentFinePrint/PaymentFinePrint';
import useApplePayConfig from '@alltrails/payment/hooks/useApplePayConfig';
import createSubscription from '@alltrails/payment/utils/createSubscription';
import createSubscriptionUpgrade from '@alltrails/payment/utils/createSubscriptionUpgrade';
import getPlusCheckoutTrialDurationText from '@alltrails/payment/utils/getPlusCheckoutTrialDurationText';
import handlePaymentFormSuccess from '@alltrails/payment/utils/handlePaymentFormSuccess';
import { getDiscountPercentage, getPriceTotal, isTrial } from '@alltrails/payment/utils/plan';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type { SubscriptionTier } from '@alltrails/types/SubscriptionTier';
import styles from './CheckoutModal.module.scss';

function promotionFinePrint(couponCode: string) {
  if (couponCode.startsWith('tmt24')) {
    return (
      <Typography color="secondary" variant="paragraph100" component="div">
        Limited-time offer; qualifying wireless plan required. See T-Mobile Tuesdays in T Life app for details.
      </Typography>
    );
  }

  return null;
}

type CheckoutModalBaseProps = {
  ariaLabelledBy: string;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  heading: ReactNode;
  plan: Plan;
  isGooglePayAvailable?: boolean;
  setBillingCountry: (country: CountryCode) => void;
  setCurrencyCode: (currencyCode: CurrencyCode) => void;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual: () => void;
  subheading?: ReactNode;
  userId: number;
  setNonTrialPlan: (plan: Plan) => void;
  setPlan: (plan: Plan) => void;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  subscriptionTier: SubscriptionTier;
  purchaseTier: PurchaseTier;
  trigger: UpsellTrigger;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

function CheckoutModalBase({
  ariaLabelledBy,
  billedHeading,
  billedSubheading,
  billedSubheadingRight,
  billedToday,
  billingCountry,
  buttonText,
  coupon,
  currencyCode,
  heading,
  navigateToWelcome,
  onSuccess,
  isGooglePayAvailable,
  plan,
  purchaseTier,
  setBillingCountry,
  setCoupon,
  setCurrencyCode,
  setNonTrialPlan,
  setPlan,
  setPlanToAnnual,
  subheading,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutModalBaseProps) {
  const setUser = useSetUser();
  const user = useUser()!;
  const experiments = useExperiments();
  const is14DayTrialEnabled = experiments['web-growth-rm-14-day-trial']?.value === 'on' && isTrial(plan, coupon);

  const canUpgradeExistingSubscription = user.canUpgradeExistingSubscription;

  const applePayConfig = useApplePayConfig({
    canUpgradeExistingSubscription,
    coupon,
    plan,
    purchaseTier,
    subscription: subscriptionsNewResponse.subscription,
    whenExpired: subscriptionsNewResponse.whenExpired
  });

  return (
    <CheckoutBase
      applePayConfig={applePayConfig}
      ariaLabelledBy={ariaLabelledBy}
      billedHeading={billedHeading}
      billedSubheading={billedSubheading}
      billedSubheadingRight={billedSubheadingRight}
      billedToday={billedToday}
      billingCountry={billingCountry}
      buttonText={buttonText}
      canUpgradeExistingSubscription={canUpgradeExistingSubscription}
      coupon={coupon}
      currencyCode={currencyCode}
      finePrint={
        <PaymentFinePrint
          billingCountry={billingCountry}
          coupon={coupon}
          currencyCode={currencyCode}
          isCouponApplied={Boolean(coupon)}
          plan={plan}
          subscriptionTier={subscriptionTier}
        />
      }
      {...(coupon ? { promoText: promotionFinePrint(coupon.code) } : {})}
      heading={heading}
      isUpgrade={user.subscriptionTier === 'plus' && purchaseTier === 'peak'}
      onBillingCountryChange={(value: CountryCode, response) => {
        setBillingCountry(value);
        setCurrencyCode(response.currencyCode);
        setNonTrialPlan(response.nonTrial);

        // similar logic in pro_controllers_helper.rb for initial load wonder if we can consolidate in one spot?
        if (!coupon && (!user || user.isPromoEligible)) {
          setPlan(response.trial);
        } else {
          setPlan(response.nonTrial);
        }
      }}
      onApplePayToken={async ({ apple_pay_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              apple_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              ...(is14DayTrialEnabled && { lt14: true }),
              apple_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          coupon,
          currencyCode,
          invoiceUuid: response.invoice_id,
          subscription_event_id: response.subscription_event_id,
          paymentMethod: 'apple_pay',
          plan,
          trigger,
          user: response.user,
          userId,
          webProPurchaseLocation: 'modal'
        });

        await onSuccess?.();
        navigateToWelcome();
      }}
      onGooglePayToken={async ({ google_pay_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              google_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              ...(is14DayTrialEnabled && { lt14: true }),
              google_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          user: response.user,
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'google_pay',
          invoiceUuid: response.invoice_id,
          subscription_event_id: response.subscription_event_id,
          coupon,
          trigger
        });

        await onSuccess?.();
        navigateToWelcome();
      }}
      onPayPalToken={async ({ paypal_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              paypal_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              ...(is14DayTrialEnabled && { lt14: true }),
              paypal_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          user: response.user,
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'paypal',
          invoiceUuid: response.invoice_id,
          subscription_event_id: response.subscription_event_id,
          coupon,
          trigger
        });

        await onSuccess?.();
        navigateToWelcome();
      }}
      onPaymentToken={async ({ payment_method_type, recaptcha, token, tokenization_source }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              payment_method_type,
              price_code: plan.priceCode,
              recaptcha,
              token,
              tokenization_source,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              ...(is14DayTrialEnabled && { lt14: true }),
              payment_method_type,
              price_code: plan.priceCode,
              recaptcha,
              token,
              tokenization_source,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        const paymentMethod = payment_method_type === 'card' ? 'credit_card' : payment_method_type;
        handlePaymentFormSuccess({
          user: response.user,
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod,
          invoiceUuid: response.invoice_id,
          coupon,
          trigger
        });

        await onSuccess?.();
        navigateToWelcome();
      }}
      onSubmitTokenizedCardForm={async payload => {
        const { cc_token, recaptcha } = payload;
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              cc_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              ...(is14DayTrialEnabled && { lt14: true }),
              cc_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          user: response.user,
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'credit_card',
          invoiceUuid: response.invoice_id,
          subscription_event_id: response.subscription_event_id,
          coupon,
          trigger
        });

        await onSuccess?.();
        navigateToWelcome();
      }}
      plan={plan}
      purchaseTier={purchaseTier}
      isCheckoutModal
      isGooglePayAvailable={isGooglePayAvailable}
      setCoupon={setCoupon}
      setPlanToAnnual={setPlanToAnnual}
      subheading={subheading}
      subscription={subscriptionsNewResponse.subscription}
      trigger={trigger}
      webProPurchaseLocation="modal"
      whenExpired={subscriptionsNewResponse.whenExpired}
    />
  );
}

type CheckoutProps = {
  initialNonTrialPlan: Plan;
  initialPlan: Plan;
  isGooglePayAvailable?: boolean;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  purchaseTier: PurchaseTier;
  subscriptionTier: SubscriptionTier;
  trigger: UpsellTrigger;
  userId: number;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

// Since this is an experiment, it is easier to copy this code from PlusCheckout than trying to create a PlusCheckoutBase component
// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=268-59615&t=aGFQDG4oH7HNoMzN-0
export default function CheckoutModal({
  initialNonTrialPlan,
  initialPlan,
  isGooglePayAvailable,
  navigateToWelcome,
  onSuccess,
  purchaseTier,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutProps) {
  const ariaLabelledBy = useModalAriaLabelledBy();
  const [billingCountry, setBillingCountry] = useState(subscriptionsNewResponse.countryCode);
  const [coupon, setCoupon] = useState<Coupon>();
  const [currencyCode, setCurrencyCode] = useState(subscriptionsNewResponse.currencyCode);
  const [nonTrialPlan, setNonTrialPlan] = useState(initialNonTrialPlan);

  const [plan, setPlan] = useState(initialPlan);

  const experiments = useExperiments();
  const is14DayTrialEnabled = experiments['web-growth-rm-14-day-trial']?.value === 'on' && isTrial(plan, coupon);

  const requestCountryCode = useRequestCountryCode();
  const showPlusTaxesText = billingCountry === 'US' || billingCountry === 'CA';

  function setPlanToAnnual() {
    setPlan(nonTrialPlan);
  }

  const annualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;

  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24f591c20f2d3669a8ea
  // Desktop: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow?node-id=685%3A35997
  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c238b04a41125e91d0422

  if (coupon && coupon.type !== 'FreeTrialCoupon') {
    const percent = getDiscountPercentage(plan, coupon) / 100;

    function getHeading() {
      return purchaseTier === 'peak' ? (
        <FormattedMessage defaultMessage="Save {percent} on Peak" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      ) : (
        <FormattedMessage defaultMessage="Save {percent} on Plus" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      );
    }

    return (
      <CheckoutModalBase
        ariaLabelledBy={ariaLabelledBy}
        billedSubheading={<FormattedMessage defaultMessage="Discount" />}
        billedSubheadingRight={<FormattedMessage defaultMessage="Save {percent, number, ::percent}" values={{ percent }} />}
        billedToday={
          <>
            <s className={styles.s}>{annualCost}</s> <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />
          </>
        }
        billingCountry={billingCountry}
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={getHeading()}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan} // this is always non trial so we could always pass nonTrial
        purchaseTier={purchaseTier}
        isGooglePayAvailable={isGooglePayAvailable}
        setBillingCountry={setBillingCountry}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  if (isTrial(plan, coupon)) {
    const trialDurationText = getPlusCheckoutTrialDurationText({
      purchaseTier,
      couponTrialDuration: coupon?.freeTrialDuration,
      is14DayTrialEnabled
    });

    return (
      <CheckoutModalBase
        ariaLabelledBy={ariaLabelledBy}
        billedSubheading={<FormattedMessage defaultMessage="Billed after trial" />}
        billedSubheadingRight={
          <FormattedMessage
            defaultMessage="{annualCost} annually{plusTaxes, select, true { + tax} other {}}"
            values={{ annualCost, plusTaxes: showPlusTaxesText }}
          />
        }
        billedToday={<FormattedNumber currency={currencyCode} style="currency" value={0} />}
        billingCountry={billingCountry}
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={trialDurationText}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan}
        purchaseTier={purchaseTier}
        isGooglePayAvailable={isGooglePayAvailable}
        setBillingCountry={setBillingCountry}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subheading={
          requestCountryCode === 'US' ? (
            <FormattedMessage defaultMessage="Then just {annualCost} a year after your trial." values={{ annualCost }} />
          ) : (
            <FormattedMessage
              defaultMessage="Then just {annualCost} a year after your trial. No commitment, cancel anytime."
              values={{ annualCost }}
            />
          )
        }
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  function getHeading() {
    return purchaseTier === 'peak' ? (
      <FormattedMessage defaultMessage="Explore to the fullest with Peak" />
    ) : (
      <FormattedMessage defaultMessage="Adventure offline with Plus" />
    );
  }

  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c23891dd05a2a732656a0
  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24a858c4c42d0bd73f80
  return (
    <CheckoutModalBase
      ariaLabelledBy={ariaLabelledBy}
      billedToday={
        showPlusTaxesText ? (
          <FormattedMessage
            defaultMessage="{annualCost} annually{plusTaxes, select, true { + tax} other {}}"
            values={{ annualCost, plusTaxes: showPlusTaxesText }}
          />
        ) : (
          annualCost
        )
      }
      billingCountry={billingCountry}
      buttonText={<FormattedMessage defaultMessage="Upgrade" />}
      currencyCode={currencyCode}
      heading={getHeading()}
      navigateToWelcome={navigateToWelcome}
      onSuccess={onSuccess}
      plan={plan} // this is always non trial so we could always pass nonTrial
      purchaseTier={purchaseTier}
      isGooglePayAvailable={isGooglePayAvailable}
      setBillingCountry={setBillingCountry}
      setCoupon={setCoupon}
      setCurrencyCode={setCurrencyCode}
      setNonTrialPlan={setNonTrialPlan}
      setPlan={setPlan}
      setPlanToAnnual={setPlanToAnnual}
      subscriptionTier={subscriptionTier}
      trigger={trigger}
      userId={userId}
      subscriptionsNewResponse={subscriptionsNewResponse}
    />
  );
}
